import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { publicAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import './GuestJoin.css';

// ── Meeting timing info shown to participants on the join page ────────────────
function fmt(iso) {
  return new Date(iso).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });
}

function MetaItem({ label, children }) {
  return (
    <div className="meeting-meta-item">
      <span className="meta-label">{label}</span>
      <span className="meta-value">{children}</span>
    </div>
  );
}

function MeetingTimingInfo({ meeting }) {
  const isInstant   = meeting.meeting_kind === 'instant';
  const isPlanned   = meeting.meeting_kind === 'planned' || !!meeting.scheduled_for;
  const isActive    = meeting.status === 'active';
  const isScheduled = meeting.status === 'scheduled';
  const isEnded     = meeting.status === 'ended';

  const statusLabel = isActive                 ? '🟢 Live Now'
                    : isEnded                  ? '🔴 Ended'
                    : isScheduled && isInstant ? '⚡ Instant Meet'
                    : isScheduled              ? '📅 Scheduled'
                    :                            meeting.status;

  const ongoingMins = isActive && meeting.started_at
    ? Math.floor((Date.now() - new Date(meeting.started_at)) / 60000)
    : null;

  return (
    <div className="meeting-meta-grid">

      {/* Status — always shown */}
      <div className="meeting-meta-item">
        <span className="meta-label">Status</span>
        <span className={`status-badge status-${meeting.status}`}>{statusLabel}</span>
      </div>

      {/* Planned & not yet started — show the full scheduled window */}
      {isPlanned && !isActive && !isEnded && meeting.scheduled_for && (
        <MetaItem label="Meeting Window">
          {fmt(meeting.scheduled_for)}
          {meeting.scheduled_end && <> &rarr; {fmt(meeting.scheduled_end)}</>}
        </MetaItem>
      )}

      {/* Active planned meeting — show window so participants know when it ends */}
      {isActive && meeting.scheduled_for && (
        <MetaItem label="Meeting Window">
          {fmt(meeting.scheduled_for)}
          {meeting.scheduled_end && <> &rarr; {fmt(meeting.scheduled_end)}</>}
        </MetaItem>
      )}

      {/* Active meeting — show actual start time */}
      {isActive && meeting.started_at && (
        <MetaItem label="Started">{fmt(meeting.started_at)}</MetaItem>
      )}

      {/* Active without a scheduled end — show live duration */}
      {isActive && ongoingMins !== null && !meeting.scheduled_end && (
        <MetaItem label="Duration">{ongoingMins} min ongoing</MetaItem>
      )}

      {/* Ended meeting — show both actual start and end */}
      {isEnded && meeting.started_at && (
        <MetaItem label="Started">{fmt(meeting.started_at)}</MetaItem>
      )}
      {isEnded && meeting.ended_at && (
        <MetaItem label="Ended">{fmt(meeting.ended_at)}</MetaItem>
      )}

    </div>
  );
}

// ── In-app browser detection ──────────────────────────────────────────────────
// WhatsApp, Instagram, Facebook, Snapchat etc. all use embedded WebViews that
// block Android App Links. We detect them so we can prompt the user to open
// the link in Chrome (which will trigger the TWA / App Links correctly).
function detectInAppBrowser() {
  const ua = navigator.userAgent || '';
  if (/WhatsApp/i.test(ua))   return 'WhatsApp';
  if (/Instagram/i.test(ua))  return 'Instagram';
  if (/FBAN|FBAV/i.test(ua))  return 'Facebook';
  if (/Snapchat/i.test(ua))   return 'Snapchat';
  if (/Line\//i.test(ua))     return 'Line';
  // Generic Android WebView (wv flag)
  if (/Android/.test(ua) && /wv/.test(ua)) return 'an in-app browser';
  return null;
}

// Android intent URL — tries to open the VoiceMeet Pro TWA directly.
// Falls back to the original URL in Chrome if the app isn't installed.
function buildIntentUrl(currentUrl) {
  const encoded = encodeURIComponent(currentUrl);
  return `intent://${window.location.host}${window.location.pathname}#Intent;scheme=https;package=com.techscriptaid.voicemeetpro;S.browser_fallback_url=${encoded};end`;
}

export default function GuestJoin() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [meeting, setMeeting]           = useState(null);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState('');
  const [guestName, setGuestName]       = useState('');
  const [nameFromStorage, setNameFromStorage] = useState(false);
  const [joining, setJoining]           = useState(false);
  const [waitingStatus, setWaitingStatus]   = useState(null);
  const [waitingRoomId, setWaitingRoomId]   = useState(null);

  const inAppBrowser = detectInAppBrowser();
  const isAndroid    = /Android/i.test(navigator.userAgent);

  // ── Load meeting ────────────────────────────────────────────────────────────
  const loadMeeting = useCallback(async () => {
    try {
      const response = await publicAPI.getMeetingByToken(token);
      setMeeting(response.data.meeting);
    } catch {
      setError('Meeting not found or invalid invite link.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { loadMeeting(); }, [loadMeeting]);

  // ── Restore guest name from session ────────────────────────────────────────
  useEffect(() => {
    const stored = sessionStorage.getItem(`guest_name_${token}`);
    if (stored) { setGuestName(stored); setNameFromStorage(true); }
  }, [token]);

  // ── Waiting room polling ────────────────────────────────────────────────────
  useEffect(() => {
    if (waitingStatus !== 'waiting' || !waitingRoomId) return;
    const interval = setInterval(async () => {
      try {
        const res = await publicAPI.checkWaitingStatus(waitingRoomId);
        if (res.data.status === 'approved') {
          clearInterval(interval);
          setWaitingStatus('approved');
          navigate(`/guest-meeting/${token}`, {
            state: {
              meeting:           res.data.meeting,
              token:             res.data.token,
              livekitUrl:        res.data.livekit_url,
              guestName:         guestName.trim(),
              guestId:           res.data.guest_id,
              canSeeParticipants: res.data.can_see_participants,
            },
          });
        } else if (res.data.status === 'rejected') {
          clearInterval(interval);
          setWaitingStatus('rejected');
        }
      } catch { /* keep polling */ }
    }, 3000);
    return () => clearInterval(interval);
  }, [waitingStatus, waitingRoomId, token, guestName, navigate]);

  // ── Guest join handler ──────────────────────────────────────────────────────
  const handleJoin = async (e) => {
    e.preventDefault();
    const trimmed = guestName.trim();
    if (!trimmed)            return setError('Please enter your name');
    if (trimmed.length < 2)  return setError('Name must be at least 2 characters');
    if (trimmed.length > 50) return setError('Name must be less than 50 characters');
    if (!/^[a-zA-Z0-9\s\-'.]+$/.test(trimmed)) return setError('Name contains invalid characters');

    setJoining(true);
    setError('');
    sessionStorage.setItem(`guest_name_${token}`, trimmed);

    try {
      const res = await publicAPI.requestJoin(token, trimmed);
      if (res.data.status === 'waiting') {
        setWaitingStatus('waiting');
        setWaitingRoomId(res.data.waiting_room_id);
        setJoining(false);
      } else if (res.data.status === 'approved') {
        navigate(`/guest-meeting/${token}`, {
          state: {
            meeting:           res.data.meeting,
            token:             res.data.token,
            livekitUrl:        res.data.livekit_url,
            guestName:         trimmed,
            guestId:           res.data.guest_id,
            canSeeParticipants: res.data.can_see_participants,
          },
        });
      }
    } catch (err) {
      setJoining(false);
      setError(err.response?.data?.error || 'Failed to join. Please try again.');
    }
  };

  // ── Loading ─────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="guest-join-container">
        <div className="guest-join-box">
          <div className="loading-spinner" />
          <p>Loading meeting…</p>
        </div>
      </div>
    );
  }

  // ── Meeting not found ───────────────────────────────────────────────────────
  if (!meeting) {
    return (
      <div className="guest-join-container">
        <div className="guest-join-box">
          <h2>❌ {error || 'Meeting not found'}</h2>
          <p>This invite link may be invalid or the meeting has been deleted.</p>
        </div>
      </div>
    );
  }

  // ── Meeting ended ───────────────────────────────────────────────────────────
  if (meeting.status === 'ended') {
    return (
      <div className="guest-join-container">
        <div className="guest-join-box">
          <h2>📅 Meeting Ended</h2>
          <p>This meeting has already ended and cannot be joined.</p>
        </div>
      </div>
    );
  }

  // ── ISSUE 1: Host clicked their own invite link ─────────────────────────────
  if (user && meeting.host_id === user.id) {
    return (
      <div className="guest-join-container">
        <motion.div
          className="guest-join-box host-detected-box"
          initial={{ opacity: 0, y: 20, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.4 }}
        >
          <div className="host-detected-icon">🎙️</div>
          <h2 className="host-detected-title">You're the Host</h2>
          <p className="host-detected-sub">
            This is your meeting invite link — meant to be shared with participants.
            Go to your meeting room to start or manage the session.
          </p>
          <div className="host-detected-meeting">
            <span className="meeting-label">Meeting</span>
            <span className="meeting-name">{meeting.title}</span>
          </div>
          <motion.button
            className="btn-join-guest btn-host-enter"
            onClick={() => navigate(`/meeting/${meeting.id}`)}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.97 }}
          >
            Enter Your Meeting Room →
          </motion.button>
          <p className="host-detected-note">
            Share this page link with participants — they'll see the guest join form.
          </p>
        </motion.div>
      </div>
    );
  }

  // ── ISSUE 1 (variant): Logged-in user who is NOT the host ──────────────────
  // Let them join as their registered account — better than joining as a guest.
  if (user) {
    return (
      <div className="guest-join-container">
        {inAppBrowser && (
          <InAppBrowserBanner appName={inAppBrowser} isAndroid={isAndroid} />
        )}
        <motion.div
          className="guest-join-box"
          initial={{ opacity: 0, y: 20, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.4 }}
        >
          <div className="meeting-header-guest">
            <h1>🎤 {meeting.title}</h1>
            <p className="host-info">Hosted by {meeting.host_name}</p>
          </div>
          <div className="logged-in-join">
            <div className="logged-in-avatar">
              {user.name?.[0]?.toUpperCase() || '?'}
            </div>
            <p className="logged-in-label">
              Joining as <strong>{user.name}</strong>
            </p>
            <p className="logged-in-sub">You're signed in — no guest name needed.</p>
          </div>
          <motion.button
            className="btn-join-guest"
            onClick={() => navigate(`/meeting/${meeting.id}`)}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.97 }}
          >
            🎙️ Join as {user.name}
          </motion.button>
          <div className="guest-note">
            <p>
              <button
                className="btn-link"
                onClick={() => {
                  /* Sign out context not needed — just let them use the guest flow below
                     by temporarily treating them as a guest (unusual edge case) */
                  navigate(`/invite/${token}?guest=1`);
                }}
              >
                Join as a different guest instead
              </button>
            </p>
          </div>
        </motion.div>
      </div>
    );
  }

  // ── Waiting room — waiting screen ───────────────────────────────────────────
  if (waitingStatus === 'waiting') {
    return (
      <div className="guest-join-container">
        <div className="guest-join-box waiting-room">
          <div className="waiting-animation">
            <div className="waiting-spinner" />
          </div>
          <h2>⏳ Waiting for Approval</h2>
          <p className="waiting-message">
            Hi <strong>{guestName}</strong>, please wait while the host lets you in.
          </p>
          <p className="meeting-title">Meeting: {meeting.title}</p>
          <p className="host-info">Host: {meeting.host_name}</p>
          <button onClick={() => { setWaitingStatus(null); setWaitingRoomId(null); setJoining(false); }} className="btn-cancel-waiting">
            Cancel
          </button>
        </div>
      </div>
    );
  }

  // ── Waiting room — rejected screen ──────────────────────────────────────────
  if (waitingStatus === 'rejected') {
    return (
      <div className="guest-join-container">
        <div className="guest-join-box rejected">
          <h2>❌ Request Declined</h2>
          <p>The host has declined your request to join this meeting.</p>
          <button onClick={() => { setWaitingStatus(null); setGuestName(''); }} className="btn-try-again">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // ── ISSUE 2: In-app browser banner + normal guest join form ────────────────
  return (
    <div className="guest-join-container">
      {inAppBrowser && (
        <InAppBrowserBanner appName={inAppBrowser} isAndroid={isAndroid} />
      )}
      <motion.div
        className="guest-join-box"
        initial={{ opacity: 0, y: 20, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.5, ease: [0, 0, 0.2, 1] }}
      >
        <div className="meeting-header-guest">
          <h1>🎤 {meeting.title}</h1>
          <p className="host-info">Hosted by {meeting.host_name}</p>
        </div>

        <div className="join-info">
          <p>You've been invited to join this audio meeting</p>

          <MeetingTimingInfo meeting={meeting} />

          {meeting.waiting_room_enabled && (
            <p className="waiting-room-notice">🔒 Host approval required to join</p>
          )}
        </div>

        {error && <div className="error-message">❌ {error}</div>}

        <form onSubmit={handleJoin} className="guest-form">
          <div className="form-group">
            <label>{nameFromStorage ? 'Welcome back! Rejoin as:' : 'Enter your name to join'}</label>
            <input
              type="text"
              value={guestName}
              onChange={(e) => setGuestName(e.target.value)}
              placeholder="Your name"
              required
              minLength={2}
              maxLength={50}
              disabled={joining || nameFromStorage}
              autoFocus={!nameFromStorage}
              autoComplete="name"
            />
            {nameFromStorage && (
              <button
                type="button"
                className="btn-link"
                onClick={() => { setNameFromStorage(false); sessionStorage.removeItem(`guest_name_${token}`); }}
              >
                Use a different name
              </button>
            )}
          </div>
          <motion.button
            type="submit"
            className="btn-join-guest"
            disabled={joining}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.97 }}
          >
            {joining ? 'Requesting…' : nameFromStorage ? '🎙️ Rejoin Meeting' : '🎙️ Request to Join'}
          </motion.button>
        </form>

        <div className="guest-note">
          <p>✨ No account needed — join as a guest!</p>
        </div>
      </motion.div>
    </div>
  );
}

// ── In-App Browser Banner component ──────────────────────────────────────────
function InAppBrowserBanner({ appName, isAndroid }) {
  const currentUrl = window.location.href;

  const handleOpenApp = () => {
    if (isAndroid) {
      // Try to open in VoiceMeet Pro TWA directly; fallback to Chrome
      window.location.href = buildIntentUrl(currentUrl);
    } else {
      // iOS — copy URL and instruct user
      navigator.clipboard?.writeText(currentUrl).catch(() => {});
    }
  };

  return (
    <motion.div
      className="inapp-browser-banner"
      initial={{ opacity: 0, y: -12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
    >
      <div className="inapp-banner-icon">🔗</div>
      <div className="inapp-banner-text">
        <strong>Open in VoiceMeet Pro</strong>
        <span>
          You're viewing this in {appName}.{' '}
          {isAndroid
            ? 'Tap below to open directly in the app or Chrome.'
            : 'Copy the link and open it in Safari or Chrome.'}
        </span>
      </div>
      <button className="inapp-banner-btn" onClick={handleOpenApp}>
        {isAndroid ? 'Open App' : 'Copy Link'}
      </button>
    </motion.div>
  );
}
